import { Link } from 'react-router-dom';
import { 
  MapPin, Shield, Clock, Users, Truck, 
  Thermometer, Package, Zap, CheckCircle, Phone,
  ArrowRight, Award
} from 'lucide-react';

const Home = () => {
  const trustItems = [
    { icon: MapPin, text: 'Nationwide Coverage', subtext: '48 States' },
    { icon: Shield, text: 'Fully Licensed', subtext: '& Insured' },
    { icon: Clock, text: 'Expedited', subtext: '& Team Driving' },
    { icon: Award, text: 'Safety Focused', subtext: 'Since 2014' },
  ];

  const services = [
    {
      icon: Truck,
      title: 'Dry Van',
      description: 'General freight protection from weather and debris',
      color: 'bg-[#0B2A5B]',
    },
    {
      icon: Thermometer,
      title: 'Reefer',
      description: 'Temperature-controlled for perishable goods',
      color: 'bg-[#1a4a8f]',
    },
    {
      icon: Package,
      title: 'Flatbed',
      description: 'Heavy equipment and oversized loads',
      color: 'bg-[#0B2A5B]',
    },
    {
      icon: Zap,
      title: 'Power-Only',
      description: 'Your trailer, our power unit',
      color: 'bg-[#1a4a8f]',
    },
    {
      icon: Truck,
      title: 'Box Truck',
      description: 'LTL and smaller shipments',
      color: 'bg-[#0B2A5B]',
    },
    {
      icon: Clock,
      title: 'Expedited',
      description: 'Time-critical deliveries nationwide',
      color: 'bg-[#C62828]',
    },
  ];

  const valueProps = [
    'Nationwide Coverage (48 States)',
    'Multi-Equipment Carrier',
    'Expedited & Team Driving',
    'Professional Dispatch Communication',
    'Reliable Since 2014',
    'Fully Licensed & Insured',
  ];

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="hero-section relative min-h-[90vh] flex items-center"
        style={{
          backgroundImage: 'url("/1.jpeg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="hero-overlay absolute inset-0 bg-gradient-to-r from-[#0B2A5B]/95 via-[#0B2A5B]/80 to-[#0B2A5B]/40"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="inline-block text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 animate-fade-in-up">
              Nationwide Freight Transportation
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6 font-['Montserrat'] animate-fade-in-up delay-100">
              Reliable Freight <br />
              <span className="text-[#FFD700]">Across All 48 States</span>
            </h1>
            <p className="text-lg text-white/80 mb-8 max-w-xl animate-fade-in-up delay-200">
              Nationwide logistics solutions since 2014. Professional dispatch, multi-equipment fleet, and expedited services for your most critical shipments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center justify-center space-x-2"
                data-testid="hero-quote-btn"
              >
                <span>Request a Quote</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="tel:773-344-7748"
                className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white bg-white/10 hover:bg-white hover:text-[#0B2A5B] font-semibold uppercase tracking-wide px-8 py-3.5 rounded-sm transition-all"
                data-testid="hero-call-btn"
              >
                <Phone className="h-4 w-4" />
                <span>Call Dispatch</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#F8FAFC] border-y border-slate-200" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-200">
            {trustItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-center py-6 px-4 space-x-3"
              >
                <div className="bg-[#0B2A5B] p-2.5 rounded-sm">
                  <item.icon className="h-5 w-5 text-[#FFD700]" />
                </div>
                <div>
                  <p className="text-[#0B2A5B] font-bold text-sm font-['Montserrat']">
                    {item.text}
                  </p>
                  <p className="text-[#6B7280] text-xs">{item.subtext}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-white" data-testid="about-preview">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                About Douala Express
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A5B] tracking-tight mb-6 font-['Montserrat']">
                Your Trusted Partner in Freight Transportation
              </h2>
              <p className="text-[#6B7280] mb-6 leading-relaxed">
                Since 2014, Douala Express Inc. has been providing reliable, professional freight transportation services across the continental United States. Our commitment to safety, communication, and on-time delivery has made us a preferred carrier for brokers and shippers nationwide.
              </p>
              <p className="text-[#6B7280] mb-8 leading-relaxed">
                With a versatile fleet and experienced team drivers, we handle everything from temperature-sensitive goods to oversized flatbed loads. Our dispatch team is available around the clock to ensure your freight reaches its destination safely and on time.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center space-x-2 text-[#0B2A5B] font-semibold hover:text-[#C62828] transition-colors"
                data-testid="about-learn-more"
              >
                <span>Explore Our Services</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src="/2.jpeg"
                  alt="Douala Express Fleet"
                  className="w-full h-[400px] object-cover rounded-sm shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-[#0B2A5B] text-white p-6 rounded-sm shadow-xl">
                  <p className="text-3xl font-black font-['Montserrat']">10+</p>
                  <p className="text-sm text-white/80">Years of Service</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="services-grid">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C62828] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              What We Offer
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A5B] tracking-tight font-['Montserrat']">
              Our Transportation Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card bg-white border border-slate-100 p-8 rounded-sm group"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`${service.color} w-14 h-14 flex items-center justify-center rounded-sm mb-6 group-hover:bg-[#C62828] transition-colors`}>
                  <service.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0B2A5B] mb-3 font-['Montserrat']">
                  {service.title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/services"
              className="btn-secondary inline-flex items-center space-x-2"
              data-testid="view-all-services-btn"
            >
              <span>View All Services</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#0B2A5B]" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-8 font-['Montserrat']">
                The Douala Express Advantage
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {valueProps.map((prop, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 bg-white/5 p-4 rounded-sm"
                  >
                    <CheckCircle className="h-5 w-5 text-[#FFD700] flex-shrink-0" />
                    <span className="text-white/90 text-sm font-medium">
                      {prop}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="/3.jpeg"
                alt="Logistics Operations"
                className="w-full h-[400px] object-cover rounded-sm"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A5B]/50 to-transparent rounded-sm"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner py-16" data-testid="cta-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Montserrat']">
            Need a Dependable Nationwide Carrier?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Get in touch with our dispatch team today for competitive rates and reliable service.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-[#C62828] px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-slate-100 transition-all shadow-lg inline-flex items-center justify-center space-x-2"
              data-testid="cta-request-quote"
            >
              <span>Request a Quote</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:773-344-7748"
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-white hover:text-[#C62828] transition-all inline-flex items-center justify-center space-x-2"
              data-testid="cta-call-now"
            >
              <Phone className="h-4 w-4" />
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
